import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, Zap, Users, Shield, Search, Monitor, Target, Star } from "lucide-react";
import { useChatbotConfig } from "../../hooks/useChatbotConfig";

export function BestPracticesTab() {
  const { config } = useChatbotConfig();
  const isConfigured = Boolean(config.businessId && config.isEnabled);

  const practiceCategories = [
    {
      id: 'performance',
      title: 'Performance Optimization',
      icon: <Zap className="w-6 h-6 text-blue-600" />,
      color: 'blue',
      practices: [
        {
          title: 'Use Async Loading',
          description: 'Always include the async attribute to prevent blocking page rendering',
          example: '<script src="..." async></script>',
          impact: 'High',
          difficulty: 'Easy'
        },
        {
          title: 'Place at End of Body',
          description: 'Load widget after main content to prioritize page speed',
          example: 'Place script just before </body> tag',
          impact: 'Medium',
          difficulty: 'Easy'
        },
        {
          title: 'Minimize Multiple Instances',
          description: 'Use one widget per page to avoid conflicts and performance issues',
          example: 'Single script tag per page',
          impact: 'Medium',
          difficulty: 'Easy'
        },
        {
          title: 'Monitor Loading Time',
          description: 'Track widget load performance in production',
          example: 'Use browser dev tools or monitoring services',
          impact: 'Low',
          difficulty: 'Medium'
        }
      ]
    },
    {
      id: 'user-experience',
      title: 'User Experience',
      icon: <Users className="w-6 h-6 text-emerald-600" />,
      color: 'emerald',
      practices: [
        {
          title: 'Strategic Positioning',
          description: 'Place widget where users naturally look for help',
          example: 'Bottom-right corner is most common and effective',
          impact: 'High',
          difficulty: 'Easy'
        },
        {
          title: 'Appropriate Auto-Open Timing',
          description: 'If using auto-open, wait for user engagement signals',
          example: 'Wait 30-60 seconds or after scroll/click events',
          impact: 'High',
          difficulty: 'Medium'
        },
        {
          title: 'Mobile Optimization',
          description: 'Ensure widget works well on mobile devices',
          example: 'Test on various screen sizes and touch interactions',
          impact: 'High',
          difficulty: 'Easy'
        },
        {
          title: 'Clear Value Proposition',
          description: 'Make it obvious what the chatbot can help with',
          example: 'Use welcome messages like "Ask me about wildlife tours!"',
          impact: 'Medium',
          difficulty: 'Easy'
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      icon: <Shield className="w-6 h-6 text-red-600" />,
      color: 'red',
      practices: [
        {
          title: 'Use HTTPS Only',
          description: 'Ensure your website uses secure connections',
          example: 'https://yourwebsite.com (not http://)',
          impact: 'High',
          difficulty: 'Easy'
        },
        {
          title: 'Regular Security Updates',
          description: 'Keep your website platform and plugins updated',
          example: 'Monthly security updates for WordPress, etc.',
          impact: 'High',
          difficulty: 'Medium'
        },
        {
          title: 'Configure CSP Headers',
          description: 'Add our domain to Content Security Policy if strict',
          example: 'script-src \'self\' https://wildy-backend-production.up.railway.app',
          impact: 'Medium',
          difficulty: 'Hard'
        },
        {
          title: 'Privacy Compliance',
          description: 'Inform users about data collection in privacy policy',
          example: 'Mention chatbot data usage in privacy documentation',
          impact: 'Medium',
          difficulty: 'Medium'
        }
      ]
    },
    {
      id: 'seo',
      title: 'SEO Considerations',
      icon: <Search className="w-6 h-6 text-purple-600" />,
      color: 'purple',
      practices: [
        {
          title: 'Async Script Loading',
          description: 'Prevent widget from affecting page speed scores',
          example: 'Google PageSpeed and Core Web Vitals won\'t be impacted',
          impact: 'High',
          difficulty: 'Easy'
        },
        {
          title: 'Structured Data Friendly',
          description: 'Widget doesn\'t interfere with structured data',
          example: 'JSON-LD and microdata remain unaffected',
          impact: 'Low',
          difficulty: 'Easy'
        },
        {
          title: 'Mobile-First Indexing',
          description: 'Ensure widget works well on mobile for Google indexing',
          example: 'Test mobile responsiveness thoroughly',
          impact: 'Medium',
          difficulty: 'Easy'
        }
      ]
    },
    {
      id: 'monitoring',
      title: 'Monitoring & Analytics',
      icon: <Monitor className="w-6 h-6 text-orange-600" />,
      color: 'orange',
      practices: [
        {
          title: 'Track Widget Usage',
          description: 'Monitor how often users interact with the chatbot',
          example: 'Check conversation starts and completion rates',
          impact: 'Medium',
          difficulty: 'Easy'
        },
        {
          title: 'Monitor Error Rates',
          description: 'Watch for console errors or failed requests',
          example: 'Set up browser console monitoring',
          impact: 'Medium',
          difficulty: 'Medium'
        },
        {
          title: 'User Feedback Collection',
          description: 'Gather feedback on chatbot helpfulness',
          example: 'Post-conversation rating system',
          impact: 'Medium',
          difficulty: 'Medium'
        },
        {
          title: 'A/B Testing',
          description: 'Test different configurations for optimal results',
          example: 'Test different positions, colors, or welcome messages',
          impact: 'Low',
          difficulty: 'Hard'
        }
      ]
    }
  ];

  const quickWins = [
    {
      title: 'Add async attribute',
      description: 'Immediate performance improvement',
      timeToImplement: '1 minute'
    },
    {
      title: 'Test on mobile devices',
      description: 'Ensure mobile users have good experience',
      timeToImplement: '5 minutes'
    },
    {
      title: 'Optimize welcome message',
      description: 'Clear, specific, helpful first impression',
      timeToImplement: '10 minutes'
    },
    {
      title: 'Set appropriate auto-open delay',
      description: 'Balance engagement with user experience',
      timeToImplement: '2 minutes'
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'border-blue-200 bg-blue-50',
      emerald: 'border-emerald-200 bg-emerald-50',
      red: 'border-red-200 bg-red-50',
      purple: 'border-purple-200 bg-purple-50',
      orange: 'border-orange-200 bg-orange-50'
    };
    return colorMap[color as keyof typeof colorMap] || 'border-slate-200 bg-slate-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-50 border border-green-200/60">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
          <Star className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Best Practices Guide</h2>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Optimize your chatbot integration for performance, user experience, and business success.
        </p>
      </div>

      {/* Quick Wins */}
      <Card className="border border-emerald-200/60 shadow-md bg-emerald-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Target className="w-6 h-6 text-emerald-600" />
            Quick Wins - Implement Today
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {quickWins.map((win, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-white border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900">{win.title}</h4>
                  <p className="text-sm text-slate-700 mb-2">{win.description}</p>
                  <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">
                    {win.timeToImplement}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Practice Categories */}
      <div className="space-y-6">
        {practiceCategories.map((category) => (
          <Card key={category.id} className={`border shadow-md ${getColorClasses(category.color)}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {category.icon}
                {category.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category.practices.map((practice, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg border border-slate-200">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-slate-900">{practice.title}</h4>
                      <div className="flex gap-2">
                        <Badge 
                          variant={practice.impact === 'High' ? 'destructive' : practice.impact === 'Medium' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {practice.impact} Impact
                        </Badge>
                        <Badge 
                          variant={practice.difficulty === 'Easy' ? 'default' : practice.difficulty === 'Medium' ? 'secondary' : 'outline'}
                          className="text-xs"
                        >
                          {practice.difficulty}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-3">{practice.description}</p>
                    <div className="p-2 bg-slate-50 rounded border text-xs font-mono text-slate-600">
                      💡 {practice.example}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Integration Checklist */}
      <Card className="border border-slate-200/60 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Pre-Launch Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Script has async attribute for performance',
              'Widget positioned appropriately (usually bottom-right)',
              'Welcome message is clear and helpful',
              'Tested on mobile devices and tablets',
              'Tested in different browsers (Chrome, Firefox, Safari)',
              'Auto-open timing is user-friendly (if enabled)',
              'Privacy policy mentions chatbot data usage',
              'Widget works with website\'s Content Security Policy',
              'Backup/fallback contact method is available',
              'Team is trained on monitoring and responding to chats'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                <div className="w-6 h-6 rounded border border-slate-300 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Success Metrics */}
      <Card className="border border-blue-200/60 shadow-md bg-blue-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Monitor className="w-6 h-6 text-blue-600" />
            Success Metrics to Track
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">📊 Engagement Metrics</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Chat initiation rate</li>
                <li>• Average conversation length</li>
                <li>• User return rate</li>
                <li>• Peak usage hours</li>
              </ul>
            </div>
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">⚡ Performance Metrics</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Widget load time</li>
                <li>• Page speed impact</li>
                <li>• Error rates</li>
                <li>• Mobile performance</li>
              </ul>
            </div>
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">💼 Business Metrics</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Lead generation rate</li>
                <li>• Support ticket reduction</li>
                <li>• Customer satisfaction</li>
                <li>• Conversion improvement</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Integration */}
      <div className="text-center pt-4">
        <Button 
          className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg"
          onClick={() => window.open(`/chatbot-test.html?businessId=${config.businessId}`, '_blank')}
          disabled={!isConfigured}
        >
          <Star className="w-4 h-4 mr-2" />
          Test Your Optimized Integration
        </Button>
      </div>

      {/* Final Note */}
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <strong>🎯 Remember:</strong> The best practices evolve with your users' needs. Start with these fundamentals, then iterate based on real usage data and feedback.
        </AlertDescription>
      </Alert>
    </div>
  );
} 